import { useState, useEffect, type ReactNode } from "react";
import {
  Badge,
  Button,
  Card,
  Collapse,
  OverlayTrigger,
  Spinner,
  Tab,
  Table,
  Tabs,
  Tooltip,
} from "react-bootstrap";
import { PlayerNameLink, type SetData } from "./Sets";
import type { GameStatsResponse, PlayerLookupResult } from "../../shared/types";
import { StockIcon, StockIcons, type CharacterColor } from "./StockIcon";
import { entrantInfo } from "../util/characterColors";
import { ReplayViewer } from "./ReplayViewer";
import { httpErrorMessage } from "../util/api";

function formatDuration(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

// --- GameInfo ---

export interface GameData {
  filename: string;
  gameNumber: number;
  downloadUrl: string;
  statsUrl: string;
}

function formatRatio(val: number | null): string {
  return val != null ? (val * 100).toFixed(1) + "%" : "-";
}

function formatDecimal(val: number | null): string {
  return val != null ? val.toFixed(1) : "-";
}

interface GameInfoProps {
  game: GameData;
  isExpanded: boolean;
}

function GameInfo({ game, isExpanded }: GameInfoProps) {
  const [stats, setStats] = useState<GameStatsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loading = isExpanded && !stats && !error;

  useEffect(() => {
    if (!isExpanded || stats || error) return;
    let cancelled = false;
    fetch(game.statsUrl)
      .then((res) => {
        if (!res.ok) throw new Error(httpErrorMessage(res.status));
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setStats(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [isExpanded, game.statsUrl, stats, error]);

  if (loading) {
    return (
      <div className="text-center p-3">
        <Spinner size="sm" />
      </div>
    );
  }
  if (error) return <div className="text-danger p-2">Failed to load stats: {error}</div>;
  if (!stats || stats.players.length < 2) return null;

  const [p1, p2] = stats.players;

  // [label, cell1, cell2, raw1, raw2, "high" | "low" | "none"]
  // "high" = higher is better, "low" = lower is better, "none" = no highlight
  const statRows: [
    string,
    ReactNode,
    ReactNode,
    number | null,
    number | null,
    "high" | "low" | "none",
  ][] = [
    [
      "Stocks Remaining",
      String(p1.stocksRemaining),
      String(p2.stocksRemaining),
      p1.stocksRemaining,
      p2.stocksRemaining,
      "high",
    ],
    [
      "Total Damage",
      formatDecimal(p1.totalDamage),
      formatDecimal(p2.totalDamage),
      p1.totalDamage,
      p2.totalDamage,
      "high",
    ],
    ["Kills", String(p1.killCount), String(p2.killCount), p1.killCount, p2.killCount, "high"],
    [
      "APM",
      formatDecimal(p1.inputsPerMinute),
      formatDecimal(p2.inputsPerMinute),
      p1.inputsPerMinute,
      p2.inputsPerMinute,
      "high",
    ],
    [
      "Openings / Kill",
      formatDecimal(p1.openingsPerKill),
      formatDecimal(p2.openingsPerKill),
      p1.openingsPerKill,
      p2.openingsPerKill,
      "low",
    ],
    [
      "Damage / Opening",
      formatDecimal(p1.damagePerOpening),
      formatDecimal(p2.damagePerOpening),
      p1.damagePerOpening,
      p2.damagePerOpening,
      "high",
    ],
    [
      "Neutral Win %",
      formatRatio(p1.neutralWinRatio),
      formatRatio(p2.neutralWinRatio),
      p1.neutralWinRatio,
      p2.neutralWinRatio,
      "high",
    ],
    [
      "Counter Hit %",
      formatRatio(p1.counterHitRatio),
      formatRatio(p2.counterHitRatio),
      p1.counterHitRatio,
      p2.counterHitRatio,
      "high",
    ],
    [
      "L-Cancel %",
      formatRatio(p1.lCancelSuccessRate),
      formatRatio(p2.lCancelSuccessRate),
      p1.lCancelSuccessRate,
      p2.lCancelSuccessRate,
      "high",
    ],
    [
      "Wavedashes",
      String(p1.wavedashCount),
      String(p2.wavedashCount),
      p1.wavedashCount,
      p2.wavedashCount,
      "none",
    ],
    [
      "Dash Dances",
      String(p1.dashDanceCount),
      String(p2.dashDanceCount),
      p1.dashDanceCount,
      p2.dashDanceCount,
      "none",
    ],
    [
      "Ledge Grabs",
      String(p1.ledgegrabCount),
      String(p2.ledgegrabCount),
      p1.ledgegrabCount,
      p2.ledgegrabCount,
      "none",
    ],
    ["Rolls", String(p1.rollCount), String(p2.rollCount), p1.rollCount, p2.rollCount, "none"],
  ];

  return (
    <div>
      <Table size="sm" responsive className="mb-0" style={{ tableLayout: "fixed" }}>
        <thead>
          <tr>
            <th style={{ width: "30%" }}></th>
            <th className="text-truncate" style={{ width: "35%" }}>
              <StockIcon character={p1.characterName} color={p1.characterColor} size={24} />
              <span className="ms-2">{p1.displayName}</span>
            </th>
            <th className="text-truncate" style={{ width: "35%" }}>
              <StockIcon character={p2.characterName} color={p2.characterColor} size={24} />
              <span className="ms-2">{p2.displayName}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {statRows.map(([label, v1, v2, raw1, raw2, direction]) => {
            let h1 = "";
            let h2 = "";
            if (direction !== "none" && raw1 != null && raw2 != null && raw1 !== raw2) {
              const p1Better = direction === "high" ? raw1 > raw2 : raw1 < raw2;
              h1 = p1Better ? "fw-bold text-success" : "";
              h2 = p1Better ? "" : "fw-bold text-success";
            }
            return (
              <tr key={label}>
                <td className="text-muted">{label}</td>
                <td className={h1}>{v1}</td>
                <td className={h2}>{v2}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}

// --- VideoEmbed ---

interface VideoEmbedProps {
  videoUrl: string;
}

function toEmbedUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.hostname === "www.youtube.com" || parsed.hostname === "youtube.com") {
      const v = parsed.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}`;
      const liveMatch = parsed.pathname.match(/^\/live\/(.+)/);
      if (liveMatch) return `https://www.youtube.com/embed/${liveMatch[1]}`;
    }
    if (parsed.hostname === "youtu.be") {
      return `https://www.youtube.com/embed${parsed.pathname}`;
    }
    return url;
  } catch {
    return null;
  }
}

function VideoEmbed({ videoUrl }: VideoEmbedProps) {
  const embedUrl = toEmbedUrl(videoUrl);
  if (!embedUrl) return null;

  return (
    <Card>
      <Card.Body>
        <div className="ratio ratio-16x9">
          <iframe src={embedUrl} title="Set VOD" allowFullScreen />
        </div>
      </Card.Body>
    </Card>
  );
}

// --- PlayerTag ---

function PlayerTag({
  port,
  characters,
  name,
  won,
  publicId,
  onOpenPlayer,
}: {
  port: number | null | undefined;
  characters: CharacterColor[];
  name: string;
  won: boolean;
  publicId?: string | null;
  onOpenPlayer?: (publicId: string) => void;
}) {
  return (
    <div className="d-flex align-items-center gap-2">
      {port != null && (
        <span className={`badge rounded-pill port-${port}`} style={{ fontSize: "0.7rem" }}>
          P{port}
        </span>
      )}
      <StockIcons characters={characters} size={24} />
      <span className={`fs-5 ${won ? "fw-bold" : ""}`}>
        <PlayerNameLink name={name} publicId={publicId} onOpenPlayer={onOpenPlayer} />
      </span>
    </div>
  );
}

// --- SetInfo ---

interface SetInfoProps {
  set: SetData;
  /** Raw entrant name → canonical player identity (or null if unresolved), for clickable names. */
  playerLookup?: PlayerLookupResult;
  onOpenPlayer?: (publicId: string) => void;
}

export function SetInfo({ set, playerLookup, onOpenPlayer }: SetInfoProps) {
  const [expandedGames, setExpandedGames] = useState<Set<number>>(new Set());

  const toggleGame = (gameNumber: number) => {
    setExpandedGames((prev) => {
      const next = new Set(prev);
      if (next.has(gameNumber)) next.delete(gameNumber);
      else next.add(gameNumber);
      return next;
    });
  };

  const games: GameData[] = set.games.map((filename, i) => ({
    filename,
    gameNumber: i + 1,
    downloadUrl: `/api/download/${set.folder}/${filename}`,
    statsUrl: `/api/game-stats/${set.folder}/${filename}`,
  }));

  // Build a lookup from filename -> summary for the accordion headers
  const summaryMap = new Map(set.gameSummaries.map((s) => [s.filename, s]));

  return (
    <div className="d-flex flex-column gap-3 h-100">
      <div className="pb-3">
        <div className="d-flex align-items-start gap-2">
          <div className="flex-grow-1">
            <div className="text-muted small">{set.startgg.event.name}</div>
            <h4 className="mb-1">{set.startgg.set.fullRoundText}</h4>
            <div className="text-muted small mb-3">
              Best of {set.bestOf} - {formatDuration(set.durationMs)}
            </div>
          </div>
          {set.games.length > 0 && (
            <Button
              as="a"
              href={`/api/download-set/${set.folder}`}
              variant="primary"
              size="sm"
              download
              className="flex-shrink-0"
            >
              <i className="bi bi-download me-1" /> Download SLPs
            </Button>
          )}
        </div>

        {(() => {
          const p1 = set.players.entrant1[0];
          const p2 = set.players.entrant2[0];
          const p1Slot = set.finalScore.slots.find((s) => s.displayNames.includes(p1?.name ?? ""));
          const p2Slot = set.finalScore.slots.find((s) => s.displayNames.includes(p2?.name ?? ""));
          const score1 = p1Slot?.score ?? 0;
          const score2 = p2Slot?.score ?? 0;

          const p1Info = entrantInfo(p1Slot, set.gameSummaries);
          const p2Info = entrantInfo(p2Slot, set.gameSummaries);
          const p1Display: CharacterColor[] =
            p1Info.characters.length > 0
              ? p1Info.characters
              : p1.characters.map((name) => ({ name }));
          const p2Display: CharacterColor[] =
            p2Info.characters.length > 0
              ? p2Info.characters
              : p2.characters.map((name) => ({ name }));

          return (
            <div className="mb-3">
              <div className="d-flex align-items-center gap-3 mb-2">
                <span
                  className={`badge rounded-pill text-bg-${score1 > score2 ? "success" : "danger"} fs-4 px-3`}
                >
                  {score1}
                </span>
                <PlayerTag
                  port={p1Info.port}
                  characters={p1Display}
                  name={p1.name}
                  won={score1 > score2}
                  publicId={playerLookup?.[p1.name]}
                  onOpenPlayer={onOpenPlayer}
                />
              </div>
              <hr className="my-1 opacity-25" />
              <div className="d-flex align-items-center gap-3 mt-2">
                <span
                  className={`badge rounded-pill text-bg-${score2 > score1 ? "success" : "danger"} fs-4 px-3`}
                >
                  {score2}
                </span>
                <PlayerTag
                  port={p2Info.port}
                  characters={p2Display}
                  name={p2.name}
                  won={score2 > score1}
                  publicId={playerLookup?.[p2.name]}
                  onOpenPlayer={onOpenPlayer}
                />
              </div>
            </div>
          );
        })()}
      </div>

      {games.length > 0 && (
        <div className="d-flex flex-column gap-2">
          {games.map((g) => {
            const isExpanded = expandedGames.has(g.gameNumber);
            const summary = summaryMap.get(g.filename);
            const summaryText = summary
              ? `${Math.floor(summary.durationSeconds / 60)}m ${Math.round(summary.durationSeconds % 60)}s - ${summary.stageName}`
              : null;
            return (
              <Card key={g.filename} className={isExpanded ? "border-primary" : ""}>
                <div
                  role="button"
                  className="d-flex align-items-center px-3 py-2 gap-2"
                  onClick={() => toggleGame(g.gameNumber)}
                  aria-expanded={isExpanded}
                >
                  <Badge pill bg={isExpanded ? "primary" : "secondary"} className="flex-shrink-0">
                    {g.gameNumber}
                  </Badge>
                  <span className="flex-grow-1 text-truncate">
                    {summaryText ?? `Game ${g.gameNumber}`}
                  </span>
                  <i className={`bi bi-chevron-${isExpanded ? "up" : "down"} text-muted`} />
                </div>
                <Collapse in={isExpanded}>
                  <div>
                    <div className="border-top px-3 py-2">
                      <div className="d-flex justify-content-end mb-2">
                        <Button as="a" href={g.downloadUrl} variant="primary" size="sm" download>
                          <i className="bi bi-download me-1" /> Download SLP
                        </Button>
                      </div>
                      {/* mountOnEnter defers loading the replay iframe until the
                          Replay tab is actually opened. */}
                      <Tabs defaultActiveKey="stats" mountOnEnter className="mb-2">
                        <Tab eventKey="stats" title="Stats">
                          <GameInfo game={g} isExpanded={isExpanded} />
                        </Tab>
                        <Tab
                          eventKey="replay"
                          title={
                            <>
                              <img
                                src="/slippilab.png"
                                alt=""
                                width={16}
                                height={16}
                                className="me-1 align-text-bottom"
                              />
                              Replay
                            </>
                          }
                        >
                          <ReplayViewer replayUrl={g.downloadUrl} />
                        </Tab>
                      </Tabs>
                    </div>
                  </div>
                </Collapse>
              </Card>
            );
          })}
        </div>
      )}

      {set.videoUrl && (
        <div className="mt-auto">
          <div className="d-flex gap-1">
            <h4>Video</h4>

            <OverlayTrigger
              placement="top"
              popperConfig={{ strategy: "fixed" }}
              overlay={(props) => (
                <Tooltip id={`copy-vod-url-tooltip-${set.id}`} {...props}>
                  Copy URL to clipboard
                </Tooltip>
              )}
            >
              <Button
                onClick={() => navigator.clipboard.writeText(set.videoUrl!)}
                variant="outline-secondary"
                size="sm"
                aria-label="Copy video URL"
                className="d-inline-flex align-items-center gap-1 p-0 bi bi-copy"
              />
            </OverlayTrigger>

            <OverlayTrigger
              placement="top"
              popperConfig={{ strategy: "fixed" }}
              overlay={(props) => (
                <Tooltip id={`open-vod-youtube-tooltip-${set.id}`} {...props}>
                  Open video in new tab
                </Tooltip>
              )}
            >
              <Button
                as="a"
                href={set.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                variant="outline-secondary"
                size="sm"
                aria-label="Open video in new tab"
                className="d-inline-flex align-items-center gap-1 p-0 bi bi-youtube"
              />
            </OverlayTrigger>
          </div>
          <VideoEmbed videoUrl={set.videoUrl} />
        </div>
      )}
    </div>
  );
}
